import { useState, useCallback, useEffect } from 'react';
import { useGetFeedQuery } from '../../store/services/feed';
import { useSessionHash } from '../../hooks/useSessionHash';

// Тип промпта для frontend
export type Prompt = {
    id: string;
    title: string;
    description: string;
    categories: string[];
    prompt: string;
    prompt_for_example: string;
    example: string;
    imageName: string;
    imageData: {
        urlOptimized: string;
        colors: {
            MainColor: string;
        };
    };
    video: string;
    likes: number;
    visits: number;
    // Properties expected by PromptCard component
    isEditorial: boolean;
    avatar: string;
    image: string;
    isLiked: boolean;
    tags: string[]; // alias for categories
};

type UsePromptsDataReturn = {
    /** Текущий список промптов */
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    prompts?: any[];
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    courses?: any;
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    challenges?: any;
    /** Состояние загрузки */
    loading: boolean;
    /** Есть ли еще данные для загрузки */
    hasMore: boolean;
    /** Функция для загрузки следующей порции данных */
    loadMore: () => Promise<void>;
    /** Функция для сброса данных */
    reset?: () => void;
};

/**
 * Хук для работы с данными ленты с поддержкой бесконечной подгрузки
 * @param pageSize - Количество элементов на одной странице
 * @param categories - Массив категорий для фильтрации
 * @param instanceId - Уникальный идентификатор экземпляра хука для генерации уникального hash
 * @returns Объект с данными и методами для управления лентой
 */
export const useFeedData = (
    pageSize: number = 20,
    categories?: string[],
    instanceId?: string,
): UsePromptsDataReturn => {
    const [nextCursor, setNextCursor] = useState<string | undefined>();
    const { hash } = useSessionHash();

    // Создаем финальный hash с учетом instanceId
    const finalHash = instanceId ? `${instanceId}_${hash}` : hash;

    const { data, isFetching, isLoading } = useGetFeedQuery({
        cursor: nextCursor,
        limit: pageSize,
        hash: finalHash,
        categories,
    });

    // Функция для загрузки следующей страницы
    const loadMore = useCallback(async (): Promise<void> => {
        if (data?.next_cursor && data.has_more_prompts) {
            setNextCursor(data.next_cursor);
        }
    }, [data?.next_cursor, data?.has_more_prompts]);

    // Сброс курсора при изменении категорий
    useEffect(() => {
        setNextCursor(undefined);
    }, [categories]);

    return {
        prompts: data?.prompts || [],
        courses: data?.courses || [],
        challenges: data?.challenges || [],
        loading: isLoading || isFetching,
        hasMore: data?.has_more_prompts ?? true,
        loadMore,
    };
};
